/**
 * Custom hook for Fleet Overview
 * Handles state and API calls for vehicle fleet management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import vehiclesService, { Vehicle, Pagination } from '@/services/vehicles/vehiclesService';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import type { StatusFilter, ConnectedFilter, PresetFilter, FuelStatus } from '@/types/fleet';
import { useAuthStore } from '@/store/authStore';
import {
  fleetOverviewSessionKey,
  parseFleetOverviewSession,
  type FleetOverviewSessionPayload,
} from '@/utils/fleetOverviewSession';
/** Valt fordon i fordonslistan (regnr + id för stabilt fordonskort-API). */
export interface FleetVehicleCardSelection {
  registrationNumber: string;
  vehicleId: number;
}

const isStatusFilter = (v: string): v is StatusFilter => v === '' || v === 'ACTIVE' || v === 'INACTIVE';
const isConnectedFilter = (v: string): v is ConnectedFilter =>
  v === '' || v === 'connected' || v === 'disconnected';
const isPresetFilter = (v: string): v is PresetFilter =>
  v === '' || v === 'poolcars' || v === 'mil' || v === 'tax-due' || v === 'inspection-due';

function withReg(t: (key: string) => string, key: string, reg: string): string {
  return t(key).replace(/\{\{reg\}\}/g, reg);
}

export const useFleetOverview = (t: (key: string) => string) => {
  const userId = useAuthStore((s) => s.user?.id);
  const [hydratedFromSession, setHydratedFromSession] = useState(false);
  const restoredPageRef = useRef(1);
  const firstListLoadAfterHydrateRef = useRef(true);
  const activatingVehicleIdsRef = useRef<Set<string>>(new Set());
  const vehicleLoadGenRef = useRef(0);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState<FleetVehicleCardSelection | null>(null);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [showCarInfoFilterModal, setShowCarInfoFilterModal] = useState(false);
  const [deviceLinkVehicle, setDeviceLinkVehicle] = useState<Vehicle | null>(null);
  const [carInfoFilterIds, setCarInfoFilterIds] = useState<number[] | null>(null);

  // Fuel/EV status
  const [fuelStatus, setFuelStatus] = useState<Record<string, FuelStatus>>({});
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ACTIVE');
  const [connectedFilter, setConnectedFilter] = useState<ConnectedFilter>('');
  const [organizationFilter, setOrganizationFilter] = useState<string>('');
  const [presetFilter, setPresetFilter] = useState<PresetFilter>('');
  // Fordonsstatus (valbar status per toppnod). '' = alla, 'none' = ingen status, annars id som sträng.
  const [usageStatusFilter, setUsageStatusFilter] = useState<string>('');
  const [usageStatusOptions, setUsageStatusOptions] = useState<{ id: number; name: string }[]>([]);

  // Server-side sortering: hela registret sorteras (inte bara laddad sida). null = standard (regnr ASC).
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Per-kolumn filter (server-side): nyckel = kolumnens filterKey, värde = fritext.
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  // Om filterraden (textbox per kolumn) är påslagen.
  const [columnFiltersEnabled, setColumnFiltersEnabled] = useState(false);

  // Hämta valbara fordonsstatusar för filtret (per toppnod; följer ev. vald organisation).
  useEffect(() => {
    let cancelled = false;
    const loadUsageStatuses = async () => {
      try {
        const res = await api.get<{ success: boolean; data: { id: number; name: string }[] }>(
          '/api/vehicles/usage-statuses',
          organizationFilter ? { params: { organizationId: organizationFilter } } : undefined
        );
        if (!cancelled) {
          setUsageStatusOptions(res.data?.success ? res.data.data || [] : []);
        }
      } catch {
        if (!cancelled) setUsageStatusOptions([]);
      }
    };
    loadUsageStatuses();
    return () => {
      cancelled = true;
    };
  }, [organizationFilter]);

  // Läs tillbaka filter/sökning från sessionStorage (samma flik-session; rensas vid ny login).
  useEffect(() => {
    if (!userId) {
      setHydratedFromSession(true);
      return;
    }
    firstListLoadAfterHydrateRef.current = true;
    restoredPageRef.current = 1;
    try {
      const snap = parseFleetOverviewSession(sessionStorage.getItem(fleetOverviewSessionKey(userId)));
      if (snap) {
        setSearch(snap.search);
        setSearchInput(snap.searchInput);
        setOrganizationFilter(snap.organizationFilter);
        if (isStatusFilter(snap.statusFilter)) setStatusFilter(snap.statusFilter);
        if (isConnectedFilter(snap.connectedFilter)) setConnectedFilter(snap.connectedFilter);
        if (isPresetFilter(snap.presetFilter)) setPresetFilter(snap.presetFilter);
        if (typeof snap.usageStatusFilter === 'string') setUsageStatusFilter(snap.usageStatusFilter);
        setCarInfoFilterIds(
          snap.carInfoFilterIds != null && snap.carInfoFilterIds.length > 0 ? snap.carInfoFilterIds : null
        );
        setSortBy(snap.sortBy);
        setSortDir(snap.sortDir);
        setColumnFilters(snap.columnFilters || {});
        setColumnFiltersEnabled(snap.columnFiltersEnabled === true);
        restoredPageRef.current = snap.listPage;
      }
    } catch {
      /* ignore */
    }
    setHydratedFromSession(true);
  }, [userId]);

  // Spara läge när användaren ändrar filter/sök/sida
  useEffect(() => {
    if (!hydratedFromSession || !userId) return;
    const payload: FleetOverviewSessionPayload = {
      v: 1,
      search,
      searchInput,
      organizationFilter,
      statusFilter,
      connectedFilter,
      presetFilter,
      usageStatusFilter,
      carInfoFilterIds,
      listPage: pagination.page,
      sortBy,
      sortDir,
      columnFilters,
      columnFiltersEnabled,
    };
    try {
      sessionStorage.setItem(fleetOverviewSessionKey(userId), JSON.stringify(payload));
    } catch {
      /* ignore */
    }
  }, [
    hydratedFromSession,
    userId,
    search,
    searchInput,
    organizationFilter,
    statusFilter,
    connectedFilter,
    presetFilter,
    usageStatusFilter,
    carInfoFilterIds,
    pagination.page,
    sortBy,
    sortDir,
    columnFilters,
    columnFiltersEnabled,
  ]);

  // Load fuel status
  const loadFuelStatus = useCallback(async (vehicleList: Vehicle[]) => {
    if (vehicleList.length === 0) return;
    
    const vehicleIds = vehicleList.map(v => v.id);
    const status = await vehiclesService.getVehicleFuelStatus(vehicleIds);
    setFuelStatus(status);
  }, []);

  // Load vehicles
  const loadVehicles = useCallback(async (
    page: number = 1, 
    searchTerm: string = '',
    status: StatusFilter = statusFilter,
    orgId: string = organizationFilter,
    filterVehicleIds: number[] | null = carInfoFilterIds,
    connected: ConnectedFilter = connectedFilter,
    currentPresetFilter: PresetFilter = presetFilter,
    currentUsageStatusFilter: string = usageStatusFilter,
    currentSortBy: string | null = sortBy,
    currentSortDir: 'asc' | 'desc' = sortDir,
    currentColumnFilters: Record<string, string> = columnFilters
  ) => {
    const generation = vehicleLoadGenRef.current + 1;
    vehicleLoadGenRef.current = generation;
    setIsLoading(true);
    setError(null);
    try {
      const activeColumnFilters = columnFiltersEnabled ? currentColumnFilters : {};
      const result = await vehiclesService.getVehicles({ 
        page, 
        limit: 50,
        search: searchTerm || undefined,
        status: filterVehicleIds !== null ? undefined : (status || undefined),
        organizationId: orgId ? String(orgId) : undefined,
        includeSubOrgs: true,
        vehicleIds: filterVehicleIds === null ? undefined : (filterVehicleIds.length > 0 ? filterVehicleIds : [0]),
        connected: connected || undefined,
        presetFilter: currentPresetFilter || undefined,
        usageStatusId: currentUsageStatusFilter || undefined,
        sortBy: currentSortBy || undefined,
        sortDir: currentSortBy ? currentSortDir : undefined,
        columnFilters: activeColumnFilters,
      });
      if (generation !== vehicleLoadGenRef.current) return;
      setVehicles(result.vehicles);
      setPagination(result.pagination);
      loadFuelStatus(result.vehicles);
    } catch (err: unknown) {
      if (generation !== vehicleLoadGenRef.current) return;
      setError(err as Error);
      toast.error(t('fleet.error'));
    } finally {
      if (generation === vehicleLoadGenRef.current) setIsLoading(false);
    }
  }, [t, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter, sortBy, sortDir, columnFilters, columnFiltersEnabled, loadFuelStatus]);

  // Ladda lista när filter ändrats (efter hydrering). Första gången: ev. sparad sida; därefter alltid sida 1.
  useEffect(() => {
    if (!hydratedFromSession) return;
    const page = firstListLoadAfterHydrateRef.current ? restoredPageRef.current : 1;
    firstListLoadAfterHydrateRef.current = false;
    loadVehicles(page, search, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter, sortBy, sortDir, columnFilters);
  }, [hydratedFromSession, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter, sortBy, sortDir, columnFilters, loadVehicles]);

  // Handle search
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    loadVehicles(1, searchInput, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter);
  }, [searchInput, loadVehicles, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter]);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearch('');
    loadVehicles(1, '', statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter);
  }, [loadVehicles, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter]);

  /** Nollställ alla filter och sökning till standard, ladda om listan direkt (undviker stale closure). */
  const clearAllFilters = useCallback(() => {
    setSearchInput('');
    setSearch('');
    setOrganizationFilter('');
    setStatusFilter('ACTIVE');
    setConnectedFilter('');
    setPresetFilter('');
    setUsageStatusFilter('');
    setCarInfoFilterIds(null);
    setSortBy(null);
    setSortDir('asc');
    setColumnFilters({});
    loadVehicles(1, '', 'ACTIVE', '', null, '', '', '', null, 'asc', {});
  }, [loadVehicles]);

  /**
   * Ändra server-side sortering. Hela registret sorteras om (inte bara laddad sida).
   * Sätter state → reload-effekten laddar om från sida 1 med ny sortering.
   */
  const handleSortChange = useCallback((nextSortBy: string, nextSortDir: 'asc' | 'desc') => {
    setSortBy(nextSortBy);
    setSortDir(nextSortDir);
  }, []);

  /**
   * Ändra ett per-kolumn filter. Tom sträng tar bort filtret.
   * Reload-effekten laddar om hela registret (server-side) med nya filter.
   */
  const handleColumnFilterChange = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (value.trim() === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }, []);

  /** Slå på/av filterraden. Vid avstängning rensas aktiva kolumnfilter. */
  const toggleColumnFilters = useCallback(() => {
    setColumnFiltersEnabled((prev) => {
      const next = !prev;
      if (!next) setColumnFilters({});
      return next;
    });
  }, []);

  // Navigate to page
  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      loadVehicles(page, search, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter);
    }
  }, [loadVehicles, pagination.totalPages, search, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter]);

  const handleOrganizationFilterChange = useCallback(
    (orgId: string) => {
      const normalized = orgId ? String(orgId) : '';
      setOrganizationFilter(normalized);
      loadVehicles(1, search, statusFilter, normalized, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter);
    },
    [loadVehicles, search, statusFilter, carInfoFilterIds, connectedFilter, presetFilter, usageStatusFilter]
  );

  // Action handlers
  const handleDeactivate = useCallback(async (vehicle: Vehicle) => {
    const confirmed = window.confirm(
      t('copy.hooks.confirmDeactivate').replace(/\{\{reg\}\}/g, vehicle.registration_number)
    );
    if (!confirmed) return;
    try {
      await api.put(`/api/vehicles/${vehicle.id}/inactivate`);
      toast.success(withReg(t, 'copy.hooks.vehicleDeactivated', vehicle.registration_number));
      loadVehicles(1, search, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || withReg(t, 'copy.hooks.vehicleDeactivateFailed', vehicle.registration_number));
    }
  }, [loadVehicles, search, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, t]);

  const handleActivate = useCallback(
    async (vehicle: Vehicle) => {
      const vehicleKey = String(vehicle.id);
      if (activatingVehicleIdsRef.current.has(vehicleKey)) return;

      const msg = t('fleet.confirmActivate').replace(/\{\{reg\}\}/g, vehicle.registration_number);
      if (!window.confirm(msg)) return;
      const reload = () =>
        loadVehicles(1, search, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter);

      activatingVehicleIdsRef.current.add(vehicleKey);
      try {
        const res = await api.put(`/api/vehicles/${vehicle.id}/activate`);
        const apiMessage = String(res?.data?.message ?? '');
        if (apiMessage.toLowerCase().includes('redan aktiv')) {
          toast.success(withReg(t, 'copy.hooks.vehicleAlreadyActive', vehicle.registration_number));
        } else {
          toast.success(
            `${vehicle.registration_number} ${t('fleet.activatedSuccess')}`
          );
        }
        reload();
      } catch (err: any) {
        toast.error(
          err?.response?.data?.error || withReg(t, 'copy.hooks.vehicleReactivateFailed', vehicle.registration_number)
        );
        reload();
      } finally {
        activatingVehicleIdsRef.current.delete(vehicleKey);
      }
    },
    [loadVehicles, search, statusFilter, organizationFilter, carInfoFilterIds, connectedFilter, t]
  );

  const handleConnectDevice = useCallback((vehicle: Vehicle) => {
    setDeviceLinkVehicle(vehicle);
  }, []);

  const handleConnectKey = useCallback((vehicle: Vehicle) => {
    toast.success(withReg(t, 'copy.hooks.connectKey', vehicle.registration_number));
  }, [t]);

  const handleSubscribe = useCallback((vehicle: Vehicle) => {
    toast.success(withReg(t, 'copy.hooks.subscribeVehicle', vehicle.registration_number));
  }, [t]);

  const handleSaveVehicleReference = useCallback(
    async (vehicle: Vehicle, raw: string) => {
      const trimmed = raw.trim();
      const next = trimmed === '' ? null : trimmed;
      const prev = (vehicle.reference_number ?? '').trim() || null;
      if ((next || '') === (prev || '')) return;
      try {
        const res = await api.put(`/api/vehicles/${vehicle.id}`, { reference_number: next });
        if (!res.data?.success) {
          throw new Error(res.data?.error || t('common.saveError'));
        }
        setVehicles((vs) =>
          vs.map((v) =>
            v.id === vehicle.id ? { ...v, reference_number: next ?? undefined } : v
          )
        );
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
            : undefined;
        toast.error(msg || t('fleet.error'));
        throw err;
      }
    },
    [t]
  );

  const applyCarInfoFilter = useCallback((ids: number[]) => {
    setSearchInput('');
    setSearch('');
    setCarInfoFilterIds(ids);
  }, []);

  /** Släpp Car.info-urvalet så listan inte ligger kvar på gamla fordons-id. */
  const clearCarInfoFilter = useCallback(() => {
    setCarInfoFilterIds(null);
  }, []);

  const filteredVehicles = vehicles;

  return {
    // State
    vehicles,
    filteredVehicles,
    isLoading,
    error,
    pagination,
    search,
    searchInput,
    setSearchInput,
    selectedVehicle,
    setSelectedVehicle,
    showAddVehicleModal,
    setShowAddVehicleModal,
    showCarInfoFilterModal,
    setShowCarInfoFilterModal,
    carInfoFilterIds,
    setCarInfoFilterIds,
    applyCarInfoFilter,
    clearCarInfoFilter,
    fuelStatus,
    statusFilter,
    setStatusFilter,
    connectedFilter,
    setConnectedFilter,
    organizationFilter,
    setOrganizationFilter,
    handleOrganizationFilterChange,
    presetFilter,
    setPresetFilter,
    usageStatusFilter,
    setUsageStatusFilter,
    usageStatusOptions,
    sortBy,
    sortDir,
    columnFilters,
    columnFiltersEnabled,
    
    // Actions
    loadVehicles,
    handleSearch,
    clearSearch,
    clearAllFilters,
    handleSortChange,
    handleColumnFilterChange,
    toggleColumnFilters,
    goToPage,
    handleDeactivate,
    handleActivate,
    handleConnectDevice,
    handleConnectKey,
    handleSubscribe,
    handleSaveVehicleReference,

    // Device link modal
    deviceLinkVehicle,
    setDeviceLinkVehicle,
  };
};
